import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import RedactionNotice from "../components/RedactionNotice";
import API from "../lib/api";
import { TableSkeleton } from "../components/Skeletons";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Plus, DownloadSimple, MagnifyingGlass, Eye, PaperPlaneTilt, Check, ArrowRight, PencilSimple, GitBranch, Trash, EnvelopeSimple, MapPin, Palette, Upload as UploadIcon, Storefront, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import GetDirectionsButton from "../components/GetDirectionsButton";
import NearbySuppliers from "../components/NearbySuppliers";
import WorkOrderEditDialog from "../components/WorkOrderEditDialog";
import PropertyAccessCard from "../components/PropertyAccessCard";
import What3WordsLink from "../components/What3WordsLink";
import SupplierPicker from "../components/SupplierPicker";
import NewSupplierDialog from "../components/NewSupplierDialog";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
import { WoNotice, WoScheduleCells, WoJobLists } from "../components/WoJobContext";
import WoStaleBanner from "../components/jobs/WoStaleBanner";
function StatusBadge({ status }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium status-${status}`}>{status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>;
}

function PriorityBadge({ priority }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium priority-${priority}`}>{priority?.toUpperCase()}</span>;
}

export default function WorkOrdersPage() {
  const { user } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [vendorUsers, setVendorUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDetail, setShowDetail] = useState(null);

  // Create WO form state
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    project_id: "", vendor_id: "", scope: "",
    source_type: "project", source_id: "",
    instructions: "", compliance_requirements: "",
    priority: "medium", due_date: "", additional_items: []
  });
  const [previewTasks, setPreviewTasks] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Amendment state
  const [showAmend, setShowAmend] = useState(null);
  const [amendForm, setAmendForm] = useState({ amendment_reason: "", scope: "", instructions: "", compliance_requirements: "", priority: "medium", due_date: "" });
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(null);

  // Delete state
  const [deleteWO, setDeleteWO] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Full WO editor (draft WOs)
  const [editingWO, setEditingWO] = useState(null);

  // Branded PDF dialog
  const [brandedFor, setBrandedFor] = useState(null);  // wo object
  const [brandedForm, setBrandedForm] = useState({ company_name: "", tagline: "", logo_url: "", footer_text: "" });
  const [brandedDownloading, setBrandedDownloading] = useState(false);
  const [brandedUploading, setBrandedUploading] = useState(false);

  // Send to Supplier dialog
  const [sendingFor, setSendingFor] = useState(null);  // wo object
  const [sendForm, setSendForm] = useState({ custom_message: "", send_email: true, send_portal: true });
  const [supplierMeta, setSupplierMeta] = useState({ loaded: false, has_portal: false, email: "", company_name: "" });
  const [sending, setSending] = useState(false);

  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const isVendor = user?.role === "vendor";

  const fetchWOs = useCallback(async () => {
    try {
      const { data } = await API.get("/works-orders");
      setWorkOrders(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchWOs();
    if (isAdmin) {
      API.get("/projects").then(({ data }) => setProjects(data.projects || [])).catch(() => {});
      API.get("/suppliers?limit=500").then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.suppliers || []);
        setSuppliers(list);
        setVendorUsers(list.filter(s => s.user_id).map(s => ({
          _id: s.user_id,
          name: s.company_name ? `${s.company_name}${s.contact_name ? ` — ${s.contact_name}` : ""}` : (s.contact_name || s.email || "Supplier"),
        })));
      }).catch(() => {});
      const mergeById = (prev, list) => { const ids = new Set(list.map(x => x.id)); return [...list, ...prev.filter(p => !ids.has(p.id))]; };
      API.get("/jobs?limit=200").then(({ data }) => setJobs(prev => mergeById(prev, data.jobs || []))).catch(() => {});
      API.get("/emergency-jobs?limit=200").then(({ data }) => setEmergencies(prev => mergeById(prev, data.jobs || []))).catch(() => {});
    }
  }, [isAdmin, fetchWOs]);

  // ── Deep-link: /work-orders?open={woId} auto-opens the WO detail dialog
  const [searchParams, setSearchParams] = useSearchParams();
  const openDetail = (wo) => {
    setShowDetail(wo);
    API.get(`/works-orders/${wo.id}`).then(({ data }) => setShowDetail((prev) => (prev && prev.id === data.id ? data : prev))).catch(() => {});
  };
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    API.get(`/works-orders/${openId}`)
      .then(({ data }) => setShowDetail(data))
      .catch(() => toast.error("Couldn't find that Works Order"))
      .finally(() => {
        // Strip the param so refreshes don't keep reopening it
        searchParams.delete("open");
        setSearchParams(searchParams, { replace: true });
      });
  }, [searchParams, setSearchParams]);

  // ── Deep-link: /work-orders?create=job|emergency&source_id={id} opens the wizard pre-filled
  useEffect(() => {
    const createType = searchParams.get("create");
    const sourceId = searchParams.get("source_id");
    if (!createType || !sourceId || !isAdmin) return;
    const path = createType === "emergency" ? `/emergency-jobs/${sourceId}` : `/jobs/${sourceId}`;
    API.get(path)
      .then(({ data }) => {
        const setter = createType === "emergency" ? setEmergencies : setJobs;
        setter(prev => (prev.some(j => j.id === data.id) ? prev : [data, ...prev]));
        setForm(f => ({ ...f, source_type: createType, source_id: data.id, project_id: "", additional_items: [{ title: data.title || "", description: "", budget: "" }],
          scope: f.scope || data.scope_of_works || "", instructions: f.instructions || data.instructions || "", due_date: f.due_date || data.schedule?.start_date || "" }));
        setStep(1);
        setShowCreate(true);
      })
      .catch(() => toast.error("Couldn't load that job for the works order"))
      .finally(() => {
        searchParams.delete("create");
        searchParams.delete("source_id");
        setSearchParams(searchParams, { replace: true });
      });
  }, [searchParams, setSearchParams, isAdmin]);

  const previewVendorTasks = async () => {
    if (!form.project_id || !form.vendor_id) return;
    setPreviewLoading(true);
    try {
      const { data } = await API.get(`/works-orders/project/${form.project_id}/vendor/${form.vendor_id}/preview`);
      setPreviewTasks(data);
    } catch { setPreviewTasks({ items: [], total_budget: 0, count: 0 }); }
    finally { setPreviewLoading(false); }
  };

  useEffect(() => {
    if (form.source_type === "project" && form.project_id && form.vendor_id && step === 2) {
      previewVendorTasks();
    }
  }, [form.project_id, form.vendor_id, form.source_type, step]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        additional_items: (form.additional_items || [])
          .filter(it => (it.title || "").trim())
          .map(it => ({ ...it, budget: parseFloat(it.budget) || 0 })),
      };
      const { data } = await API.post("/works-orders", payload);
      toast.success(`Works Order ${data.wo_number} created`);
      setShowCreate(false);
      resetForm();
      fetchWOs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create works order"); }
  };

  const resetForm = () => {
    setForm({ project_id: "", vendor_id: "", scope: "", source_type: "project", source_id: "", supplier_record_id: "", vendor_name: "", vendor_company: "", vendor_email: "", vendor_phone: "", instructions: "", compliance_requirements: "", priority: "medium", due_date: "", additional_items: [] });
    setPreviewTasks(null);
    setStep(1);
  };

  const downloadPdf = async (woId, woNumber) => {
    try {
      const response = await API.get(`/works-orders/${woId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${woNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleAction = async (woId, action) => {
    try {
      await API.put(`/works-orders/${woId}/${action}`);
      toast.success(`Works order ${action}${action === "send" ? " to supplier" : action === "approve" ? "d" : action === "complete" ? "d" : "ted"}`);
      fetchWOs();
      if (showDetail) {
        const { data } = await API.get(`/works-orders/${woId}`);
        setShowDetail(data);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
  };

  const getProjectName = (id) => projects.find(p => p.id === id)?.name || "";
  const getVendorName = (id) => vendorUsers.find(v => v._id === id)?.name || "";
  const sourceList = form.source_type === "job" ? jobs : emergencies;
  const getSourceLabel = () => {
    if (form.source_type === "project") return getProjectName(form.project_id);
    const j = sourceList.find(x => x.id === form.source_id);
    return j ? `${j.reference ? j.reference + " — " : ""}${j.title || ""}` : "";
  };
  const manualTotal = (form.additional_items || []).reduce((s, it) => s + (parseFloat(it.budget) || 0), 0);

  const handleAmend = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post(`/works-orders/${showAmend.id}/amend`, amendForm);
      toast.success(`Amendment created: ${data.wo_number}`);
      setShowAmend(null);
      setShowDetail(null);
      fetchWOs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create amendment"); }
  };

  const openAmendDialog = (wo) => {
    setAmendForm({
      amendment_reason: "",
      scope: wo.scope || "",
      instructions: wo.instructions || "",
      compliance_requirements: wo.compliance_requirements || "",
      priority: wo.priority || "medium",
      due_date: wo.due_date || ""
    });
    setShowAmend(wo);
  };

  const fetchRevisions = async (woId) => {
    try {
      const { data } = await API.get(`/works-orders/${woId}/revisions`);
      setRevisions(data);
      setShowRevisions(woId);
    } catch { toast.error("Failed to load revisions"); }
  };

  const handleDeleteWO = async () => {
    if (!deleteWO) return;
    try {
      await API.delete(`/works-orders/${deleteWO.id}`);
      toast.success("Works order permanently deleted");
      setDeleteWO(null);
      setDeleteConfirmText("");
      fetchWOs();
    } catch { toast.error("Failed to delete works order"); }
  };

  // ── Branded PDF dialog ───────────────────────────────────────────────
  const openBrandedDialog = async (wo) => {
    setBrandedFor(wo);
    try {
      const { data } = await API.get("/tenant/current");
      const b = (data && data.branding) || {};
      setBrandedForm({
        company_name: b.company_name || data?.name || "",
        tagline: b.tagline || "",
        logo_url: b.logo_url || "",
        footer_text: b.footer_text || "",
      });
    } catch {
      setBrandedForm({ company_name: "", tagline: "", logo_url: "", footer_text: "" });
    }
  };

  const handleBrandedLogoUpload = async (file) => {
    if (!file) return;
    setBrandedUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/upload?entity_type=branding&entity_id=logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const logoUrl = data.file_url || `/api/files/${data.file_id}/${data.file_name}`;
      setBrandedForm(f => ({ ...f, logo_url: logoUrl }));
      toast.success("Logo uploaded");
    } catch { toast.error("Failed to upload logo"); }
    finally { setBrandedUploading(false); }
  };

  const downloadBrandedPdf = async () => {
    if (!brandedFor) return;
    setBrandedDownloading(true);
    try {
      const response = await API.post(`/works-orders/${brandedFor.id}/pdf`, brandedForm, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${brandedFor.wo_number || "works-order"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Branded PDF downloaded");
      setBrandedFor(null);
    } catch { toast.error("Failed to download branded PDF"); }
    finally { setBrandedDownloading(false); }
  };

  // ── Send to Supplier (email + portal) ────────────────────────────────
  const openSendDialog = async (wo) => {
    setSendingFor(wo);
    setSendForm({ custom_message: "", send_email: true, send_portal: true });
    setSupplierMeta({ loaded: false, has_portal: false, email: wo.vendor_email || "", company_name: wo.vendor_company || wo.vendor_name || "" });
    // Resolve onboarded portal status for the WO's vendor
    if (wo.vendor_id) {
      try {
        const { data } = await API.get(`/suppliers/${wo.vendor_id}`);
        setSupplierMeta({
          loaded: true,
          has_portal: Boolean(data?.user_id),
          email: data?.email || wo.vendor_email || "",
          company_name: data?.company_name || wo.vendor_company || wo.vendor_name || "",
        });
        // If no portal exists, force send_portal off
        if (!data?.user_id) setSendForm(f => ({ ...f, send_portal: false }));
      } catch {
        setSupplierMeta(m => ({ ...m, loaded: true, has_portal: false }));
        setSendForm(f => ({ ...f, send_portal: false }));
      }
    } else {
      setSupplierMeta(m => ({ ...m, loaded: true, has_portal: false }));
      setSendForm(f => ({ ...f, send_portal: false }));
    }
  };

  const submitSendToSupplier = async () => {
    if (!sendingFor) return;
    if (!sendForm.send_email && !sendForm.send_portal) {
      toast.error("Choose at least one delivery method");
      return;
    }
    if (sendForm.send_email && !supplierMeta.email) {
      toast.error("Supplier has no email on file");
      return;
    }
    setSending(true);
    try {
      const { data } = await API.post(`/works-orders/${sendingFor.id}/email-supplier`, {
        custom_message: sendForm.custom_message,
        send_email: sendForm.send_email,
        send_portal: sendForm.send_portal,
      });
      const parts = [];
      if (data.email_sent) parts.push(`emailed to ${data.vendor_email}`);
      if (data.portal_pushed) parts.push("pushed to supplier portal");
      toast.success(`Works Order ${parts.join(" + ")}`);
      setSendingFor(null);
      // Refresh detail to show updated status_history
      const { data: fresh } = await API.get(`/works-orders/${sendingFor.id}`);
      setShowDetail(fresh);
      fetchWOs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send Works Order");
    } finally { setSending(false); }
  };

  const filtered = workOrders.filter(wo => {
    if (statusFilter !== "all" && wo.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return wo.wo_number?.toLowerCase().includes(q) ||
      wo.project_name?.toLowerCase().includes(q) ||
      wo.vendor_name?.toLowerCase().includes(q);
  });

  const WO_STATUSES = ["draft", "submitted", "approved", "sent_to_vendor", "completed", "superseded"];

  return (
    <div data-testid="work-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight" data-testid="works-orders-title">Works Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">{workOrders.length} total</p>
          {isVendor && <div className="mt-2 max-w-md"><RedactionNotice /></div>}
        </div>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="create-wo-btn">
            <Plus size={16} className="mr-1" /> New Works Order
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search works orders..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="wo-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="wo-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {WO_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <p className="text-muted-foreground">No works orders found</p>
          {isAdmin && <p className="text-xs text-muted-foreground mt-1">Create a works order by selecting a project and supplier.</p>}
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="wo-table">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-3 font-semibold">WO Number</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Project</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Supplier</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Tasks</th>
                <th className="text-right px-4 py-3 font-semibold hidden md:table-cell">Budget</th>
                <th className="text-left px-4 py-3 font-semibold">Priority</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(wo => (
                <tr key={wo.id} className={`hover:bg-muted/30 transition-colors ${wo.status === "superseded" ? "opacity-50" : ""}`} data-testid={`wo-row-${wo.id}`}>
                  <td className="px-4 py-3">
                    <span className="font-medium font-mono text-xs">{wo.wo_number}</span>
                    {(wo.revision_number || 0) > 0 && <span className="ml-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">R{wo.revision_number}</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">{wo.project_name || "-"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{wo.vendor_name || "-"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{wo.line_items?.length || 0}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-right text-xs font-medium">£{(wo.total_budget || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><PriorityBadge priority={wo.priority} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={wo.status} />
                      {wo.needs_reissue && wo.status !== "superseded" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200" title={`Job changed (${(wo.needs_reissue.changed_fields || []).join(", ")}) after this WO was sent`} data-testid={`wo-out-of-date-${wo.id}`}><Warning size={10} weight="fill" /> Out of date</span>}
                      {wo.last_reissue?.at && !wo.needs_reissue && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-800 dark:bg-orange-500/15 dark:text-orange-200" title="Re-issued to the supplier" data-testid={`wo-updated-${wo.id}`}>Updated {fmtDate(wo.last_reissue.at)}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(wo)} title="View details" data-testid={`view-wo-${wo.id}`}>
                        <Eye size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadPdf(wo.id, wo.wo_number)} title="Download PDF" data-testid={`download-pdf-${wo.id}`}>
                        <DownloadSimple size={14} />
                      </Button>
                      {isAdmin && wo.status === "draft" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={() => setEditingWO(wo)} title="Edit works order" data-testid={`edit-wo-${wo.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      )}
                      {isAdmin && wo.status === "draft" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={() => handleAction(wo.id, "submit")} title="Submit for approval" data-testid={`submit-wo-${wo.id}`}>
                          <PaperPlaneTilt size={14} />
                        </Button>
                      )}
                      {isAdmin && wo.status === "submitted" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" onClick={() => handleAction(wo.id, "approve")} title="Approve" data-testid={`approve-wo-${wo.id}`}>
                          <Check size={14} weight="bold" />
                        </Button>
                      )}
                      {isAdmin && wo.status === "approved" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" onClick={() => handleAction(wo.id, "send")} title="Send to supplier" data-testid={`send-wo-${wo.id}`}>
                          <ArrowRight size={14} />
                        </Button>
                      )}
                      {isAdmin && wo.vendor_email && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={async () => {
                          try {
                            await API.post(`/works-orders/${wo.id}/email-supplier`);
                            toast.success(`Emailed to ${wo.vendor_email}`);
                          } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                        }} title="Email to supplier" data-testid={`email-wo-${wo.id}`}>
                          <EnvelopeSimple size={14} />
                        </Button>
                      )}
                      {isAdmin && ["approved", "sent_to_vendor", "completed"].includes(wo.status) && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-600" onClick={() => openAmendDialog(wo)} title="Amend / Create Revision" data-testid={`amend-wo-${wo.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                      )}
                      {wo.wo_number?.includes("-R") || wo.status === "superseded" ? (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => fetchRevisions(wo.id)} title="View revisions" data-testid={`revisions-wo-${wo.id}`}>
                          <GitBranch size={14} />
                        </Button>
                      ) : null}
                      {isAdmin && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteWO(wo)} title="Delete permanently" data-testid={`delete-wo-${wo.id}`}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Works Order Dialog - Multi-step */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) resetForm(); setShowCreate(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-wo-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Create Works Order</DialogTitle>
            <DialogDescription>
              {step === 1 && "Step 1: Choose what to issue against (project, job or emergency job) and a supplier."}
              {step === 2 && (form.source_type === "project" ? "Step 2: Review supplier tasks that will be included." : "Step 2: Add the cost lines for this works order.")}
              {step === 3 && "Step 3: Add scope details and finalize."}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-4" data-testid="wo-step-indicator">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex items-center gap-1.5 ${s <= step ? "text-primary" : "text-muted-foreground"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border-2 ${s < step ? "bg-primary text-primary-foreground border-primary" : s === step ? "border-primary text-primary" : "border-border"}`}>
                  {s < step ? <Check size={12} weight="bold" /> : s}
                </div>
                {s < 3 && <div className={`w-8 h-0.5 ${s < step ? "bg-primary" : "bg-border"}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Select Source + Supplier */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Issue against *</Label>
                <Select value={form.source_type} onValueChange={v => setForm({ ...form, source_type: v, project_id: "", source_id: "", additional_items: [] })}>
                  <SelectTrigger data-testid="wo-source-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project">Project</SelectItem>
                    <SelectItem value="job">Job</SelectItem>
                    <SelectItem value="emergency">Emergency Job</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.source_type === "project" ? (
                <div>
                  <Label>Project *</Label>
                  <Select value={form.project_id} onValueChange={v => setForm({...form, project_id: v})}>
                    <SelectTrigger data-testid="wo-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>{form.source_type === "job" ? "Job *" : "Emergency Job *"}</Label>
                  <Select value={form.source_id} onValueChange={v => {
                    const j = sourceList.find(x => x.id === v);
                    setForm({ ...form, source_id: v, additional_items: j ? [{ title: j.title || "", description: "", budget: "" }] : [],
                      scope: j?.scope_of_works || form.scope, instructions: j?.instructions || form.instructions, due_date: j?.schedule?.start_date || form.due_date });
                  }}>
                    <SelectTrigger data-testid="wo-source-select"><SelectValue placeholder={form.source_type === "job" ? "Select job" : "Select emergency job"} /></SelectTrigger>
                    <SelectContent>
                      {sourceList.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">None found</div>}
                      {sourceList.map(j => (
                        <SelectItem key={j.id} value={j.id}>{j.reference ? `${j.reference} — ` : ""}{j.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Supplier *</Label>
                <div className="mt-1">
                  <SupplierPicker
                    suppliers={suppliers}
                    selectedId={form.supplier_record_id}
                    onPick={(s) => {
                      if (!s) { setForm(f => ({ ...f, supplier_record_id: "", vendor_id: "", vendor_name: "", vendor_company: "", vendor_email: "", vendor_phone: "" })); return; }
                      setForm(f => ({
                        ...f,
                        supplier_record_id: s.id,
                        vendor_id: s.user_id || "",
                        vendor_name: s.contact_name || s.name || "",
                        vendor_company: s.company_name || s.name || "",
                        vendor_email: s.email || "",
                        vendor_phone: s.phone || "",
                      }));
                    }}
                    label="Search your supplier list"
                    testIdPrefix="wo-supplier"
                  />
                </div>
                {form.supplier_record_id && !form.vendor_id && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">This supplier has no portal account — they'll receive the works order by email only.</p>
                )}
                <Button type="button" variant="outline" size="sm" className="w-full mt-2 text-xs" onClick={() => setShowNewSupplier(true)} data-testid="wo-new-supplier-btn">
                  + New supplier (not on system)
                </Button>
              </div>

              {/* Nearby Suppliers — shown once a project is selected */}
              {form.source_type === "project" && form.project_id && (
                <NearbySuppliers
                  projectId={form.project_id}
                  radiusMiles={50}
                  limit={6}
                  title="Suggested nearby suppliers"
                  showTradeInput
                  showAutoPick
                  onPick={(s) => {
                    setForm(f => ({
                      ...f,
                      supplier_record_id: s.id || "",
                      vendor_id: s.user_id || "",
                      vendor_name: s.contact_name || "",
                      vendor_company: s.company_name || "",
                      vendor_email: s.email || "",
                      vendor_phone: s.phone || "",
                    }));
                    const compliance = s.compliance_status ? ` · ${s.compliance_status.replace("_", " ")}` : "";
                    const portalNote = s.user_id ? "" : " They have no portal account, so the WO goes by email only.";
                    toast.success(`Picked ${s.company_name} (${s.distance_miles} mi${compliance}).${portalNote}`);
                  }}
                  data-testid="wo-nearby-suppliers"
                />
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { resetForm(); setShowCreate(false); }}>Cancel</Button>
                <Button type="button" disabled={!(form.source_type === "project" ? form.project_id : form.source_id) || !(form.vendor_id || form.supplier_record_id || form.vendor_company.trim())} onClick={() => setStep(2)} data-testid="wo-next-step-btn">
                  {form.source_type === "project" ? "Next: Preview Tasks" : "Next: Cost Lines"} <ArrowRight size={14} className="ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Preview Tasks (project) or Cost Lines (job / emergency) */}
          {step === 2 && form.source_type !== "project" && (
            <div className="space-y-4">
              <div className="border border-border rounded-md p-3 bg-muted/30" data-testid="wo-manual-lines">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Cost lines for {getSourceLabel()}
                </span>
                <div className="mt-3 space-y-2">
                  {(form.additional_items || []).map((it, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input placeholder="Line description" value={it.title}
                             onChange={e => setForm(f => ({ ...f, additional_items: f.additional_items.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x) }))}
                             data-testid={`wo-line-title-${i}`} />
                      <Input type="number" step="0.01" min="0" className="w-32" placeholder="£ 0.00" value={it.budget}
                             onChange={e => setForm(f => ({ ...f, additional_items: f.additional_items.map((x, xi) => xi === i ? { ...x, budget: e.target.value } : x) }))}
                             data-testid={`wo-line-amount-${i}`} />
                      <Button type="button" variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-red-600"
                              onClick={() => setForm(f => ({ ...f, additional_items: f.additional_items.filter((_, xi) => xi !== i) }))}
                              data-testid={`wo-line-remove-${i}`}>×</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm"
                          onClick={() => setForm(f => ({ ...f, additional_items: [...(f.additional_items || []), { title: "", description: "", budget: "" }] }))}
                          data-testid="wo-line-add-btn">+ Add line</Button>
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-xs font-semibold">{(form.additional_items || []).filter(it => (it.title || "").trim()).length} line(s)</span>
                    <span className="text-sm font-bold" data-testid="wo-manual-total">Total: £{manualTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button type="button" onClick={() => setStep(3)} data-testid="wo-next-step-2-btn">
                  Next: Finalize <ArrowRight size={14} className="ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === 2 && form.source_type === "project" && (
            <div className="space-y-4">
              <div className="border border-border rounded-md p-3 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Tasks assigned to {getVendorName(form.vendor_id)} in {getProjectName(form.project_id)}
                  </span>
                </div>
                {previewLoading ? (
                  <div className="flex justify-center py-6"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
                ) : previewTasks && previewTasks.items.length > 0 ? (
                  <div className="space-y-1" data-testid="wo-preview-tasks">
                    {previewTasks.items.map((item, idx) => (
                      <div key={item.id || `prev-${idx}`} className={`flex items-center justify-between py-1.5 px-2 rounded text-xs ${item.is_subtask ? "ml-4 bg-muted/50" : ""}`} data-testid={`wo-preview-item-${idx}`}>
                        <div className="flex-1">
                          <span className="font-medium">{item.title}</span>
                          {item.is_subtask && item.parent_title && <span className="text-muted-foreground ml-1">(sub of: {item.parent_title})</span>}
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-muted-foreground capitalize">{item.category?.replace(/_/g, " ")}</span>
                            <StatusBadge status={item.status} />
                            {item.due_date && <span className="text-muted-foreground">{fmtDate(item.due_date)}</span>}
                          </div>
                        </div>
                        <span className="font-medium ml-2">£{(item.budget || 0).toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
                      <span className="text-xs font-semibold">{previewTasks.count} task(s)</span>
                      <span className="text-sm font-bold">Total: £{(previewTasks.total_budget || 0).toLocaleString()}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground py-4 text-center">No tasks assigned to this supplier on this project. You can still create a WO with manual scope.</p>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button type="button" onClick={() => setStep(3)} data-testid="wo-next-step-2-btn">
                  Next: Finalize <ArrowRight size={14} className="ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Finalize */}
          {step === 3 && (
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="border border-border rounded-md p-3 bg-muted/20 text-xs space-y-1">
                <p><span className="font-semibold">{form.source_type === "project" ? "Project:" : form.source_type === "job" ? "Job:" : "Emergency Job:"}</span> {getSourceLabel()}</p>
                <p><span className="font-semibold">Supplier:</span> {getVendorName(form.vendor_id)}</p>
                {form.source_type === "project" ? (
                  <p><span className="font-semibold">Tasks to include:</span> {previewTasks?.count || 0} ({previewTasks?.total_budget ? `£${previewTasks.total_budget.toLocaleString()} budget` : "No budget set"})</p>
                ) : (
                  <p><span className="font-semibold">Cost lines:</span> {(form.additional_items || []).filter(it => (it.title || "").trim()).length} (£{manualTotal.toLocaleString()})</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                    <SelectTrigger data-testid="wo-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["low","medium","high","critical"].map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Due Date</Label><DateInput value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="wo-due-input" /></div>
              </div>
              <div><Label>Scope of Works *</Label><Textarea value={form.scope} onChange={e => setForm({...form, scope: e.target.value})} rows={3} required placeholder="Describe the scope of works..." data-testid="wo-scope-input" /></div>
              <div><Label>Instructions</Label><Textarea value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})} rows={2} placeholder="Special instructions for the supplier..." data-testid="wo-instructions-input" /></div>
              <div><Label>Compliance Requirements</Label><Textarea value={form.compliance_requirements} onChange={e => setForm({...form, compliance_requirements: e.target.value})} rows={2} placeholder="Any compliance / H&S requirements..." /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button type="submit" data-testid="submit-wo-btn">Create Works Order</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* WO Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={(open) => { if (!open) setShowDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wo-detail-dialog">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-[Chivo] flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{showDetail.wo_number}</span>
                  <StatusBadge status={showDetail.status} />
                  {(showDetail.revision_number || 0) > 0 && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Revision {showDetail.revision_number}</span>}
                </DialogTitle>
                <DialogDescription>
                  Created by {showDetail.created_by_name} on {fmtDate(showDetail.created_at)}
                  {showDetail.amendment_reason && <span className="block mt-1 text-amber-600">Amendment reason: {showDetail.amendment_reason}</span>}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {showDetail.needs_reissue && (
                  <WoStaleBanner flag={{ ...showDetail.needs_reissue, wo_id: showDetail.id, wo_number: showDetail.wo_number }} testid="wo-detail-stale-banner"
                    onReissued={() => { fetchWOs(); API.get(`/works-orders/${showDetail.id}`).then(({ data }) => setShowDetail(data)).catch(() => {}); }} />
                )}
                {showDetail.last_reissue?.at && !showDetail.needs_reissue && (
                  <p className="text-[11px] text-orange-800 dark:text-orange-200 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-800/50 rounded px-3 py-1.5" data-testid="wo-detail-reissued">
                    Re-issued to the supplier on {fmtDate(showDetail.last_reissue.at)}{(showDetail.last_reissue.changed_fields || []).length ? ` after changes to: ${showDetail.last_reissue.changed_fields.join(", ")}` : ""}.
                  </p>
                )}
                <WoNotice wo={showDetail} />
                {/* Property & Directions */}
                {(showDetail.property_address || showDetail.street_address || showDetail.property) && (
                  <div className="border border-border rounded p-3 bg-muted/20 flex items-start justify-between gap-3" data-testid="wo-detail-property">
                    <div className="flex items-start gap-2 min-w-0">
                      <MapPin size={16} className="text-primary mt-0.5 shrink-0" weight="duotone" />
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Property</p>
                        {showDetail.property_name && <p className="text-sm font-semibold truncate">{showDetail.property_name}</p>}
                        <p className="text-sm font-medium truncate">{showDetail.property_address || [showDetail.street_address, showDetail.town_city].filter(Boolean).join(", ") || showDetail.property}</p>
                        {showDetail.postcode && (
                          <p className="text-xs text-muted-foreground">{showDetail.postcode}</p>
                        )}
                        {showDetail.what3words && <div className="mt-1"><What3WordsLink words={showDetail.what3words} /></div>}
                        {showDetail.job_title && <p className="text-[11px] text-muted-foreground mt-1">Job: {showDetail.job_title}{showDetail.job_reference ? ` · ${showDetail.job_reference}` : ""}</p>}
                        {showDetail.arrived_at && (
                          <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1 font-medium" data-testid="wo-arrived-chip">
                            Supplier arrived at {new Date(showDetail.arrived_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                    </div>
                    <GetDirectionsButton
                      address={showDetail.property_address || [showDetail.street_address, showDetail.town_city, showDetail.postcode].filter(Boolean).join(", ") || showDetail.property}
                      latitude={showDetail.latitude}
                      longitude={showDetail.longitude}
                      variant="default"
                      size="sm"
                      data-testid="wo-detail-directions-btn"
                    />
                  </div>
                )}

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="border border-border rounded p-2">
                    <span className="text-muted-foreground">Project</span>
                    <p className="font-medium">{showDetail.project_name || "-"}</p>
                  </div>
                  <div className="border border-border rounded p-2">
                    <span className="text-muted-foreground">Supplier</span>
                    <p className="font-medium">{showDetail.vendor_name || "-"}</p>
                    {showDetail.vendor_company && <p className="text-muted-foreground">{showDetail.vendor_company}</p>}
                  </div>
                  <div className="border border-border rounded p-2">
                    <span className="text-muted-foreground">Priority</span>
                    <p><PriorityBadge priority={showDetail.priority} /></p>
                  </div>
                  <div className="border border-border rounded p-2">
                    <span className="text-muted-foreground">Due Date</span>
                    <p className="font-medium">{fmtDate(showDetail.due_date) || "-"}</p>
                  </div>
                  <WoScheduleCells wo={showDetail} />
                </div>

                {/* Access & on-site contact (from the linked job / property) */}
                {showDetail.job_id && <PropertyAccessCard entityType="job" entityId={showDetail.job_id} compact />}

                {/* Scope / instructions / compliance — boxed off like the job view */}
                {showDetail.scope && (
                  <div className="border-l-[3px] border-primary bg-primary/5 rounded-r-md px-3 py-2" data-testid="wo-detail-scope">
                    <p className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">Scope of Works</p>
                    <p className="text-sm whitespace-pre-wrap">{showDetail.scope}</p>
                  </div>
                )}
                {showDetail.instructions && (
                  <div className="border-l-[3px] border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30 rounded-r-md px-3 py-2" data-testid="wo-detail-instructions">
                    <p className="text-[10px] uppercase tracking-widest text-cyan-700 dark:text-cyan-300 font-semibold mb-1">Instructions</p>
                    <p className="text-sm whitespace-pre-wrap">{showDetail.instructions}</p>
                  </div>
                )}
                {showDetail.compliance_requirements && (
                  <div className="border-l-[3px] border-violet-500 bg-violet-50 dark:bg-violet-950/30 rounded-r-md px-3 py-2" data-testid="wo-detail-compliance">
                    <p className="text-[10px] uppercase tracking-widest text-violet-700 dark:text-violet-300 font-semibold mb-1">Compliance requirements</p>
                    <p className="text-sm whitespace-pre-wrap">{showDetail.compliance_requirements}</p>
                  </div>
                )}
                <WoJobLists wo={showDetail} />

                {/* Line Items */}
                {showDetail.line_items?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Task Line Items</p>
                    <div className="border border-border rounded-md overflow-hidden" data-testid="wo-detail-line-items">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border bg-muted/50">
                            <th className="text-left px-3 py-2 font-semibold">Task</th>
                            <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Category</th>
                            <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Status</th>
                            <th className="text-right px-3 py-2 font-semibold">Budget</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {showDetail.line_items.map((item, idx) => (
                            <tr key={item.id || `li-${idx}`} className={item.is_subtask ? "bg-muted/20" : ""} data-testid={`wo-detail-item-${idx}`}>
                              <td className="px-3 py-2">
                                <span className="font-medium">{item.is_subtask ? "  " : ""}{item.title}</span>
                                {item.is_subtask && item.parent_title && <span className="text-muted-foreground ml-1">(sub of: {item.parent_title})</span>}
                              </td>
                              <td className="px-3 py-2 hidden sm:table-cell text-muted-foreground capitalize">{item.category?.replace(/_/g, " ")}</td>
                              <td className="px-3 py-2 hidden sm:table-cell"><StatusBadge status={item.status} /></td>
                              <td className="px-3 py-2 text-right font-medium">£{(item.budget || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-primary/30 bg-muted/30">
                            <td colSpan={3} className="px-3 py-2 text-right font-bold">Total Budget:</td>
                            <td className="px-3 py-2 text-right font-bold">£{(showDetail.total_budget || 0).toLocaleString()}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Status History */}
                {showDetail.status_history?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Status History</p>
                    <div className="space-y-1">
                      {showDetail.status_history.map((sh, idx) => (
                        <div key={`${sh.status || ""}-${sh.date || ""}-${idx}`} className="flex items-center gap-2 text-xs">
                          <StatusBadge status={sh.status} />
                          <span className="text-muted-foreground">by {sh.by}</span>
                          <span className="text-muted-foreground">{fmtDate(sh.date)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2 pt-2 border-t border-border flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(showDetail.id, showDetail.wo_number)} data-testid="wo-detail-download-pdf">
                    <DownloadSimple size={14} className="mr-1" /> Download PDF
                  </Button>
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => openBrandedDialog(showDetail)} data-testid="wo-detail-branded-pdf">
                      <Palette size={14} className="mr-1" /> Branded PDF
                    </Button>
                  )}
                  {isAdmin && showDetail.status === "draft" && (
                    <Button variant="outline" size="sm" onClick={() => setEditingWO(showDetail)} data-testid="wo-detail-edit-wo">
                      <PencilSimple size={14} className="mr-1" /> Edit Works Order
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" onClick={() => openSendDialog(showDetail)} data-testid="wo-detail-send-supplier" className="bg-blue-600 hover:bg-blue-700">
                      <PaperPlaneTilt size={14} className="mr-1" /> Send to Supplier
                    </Button>
                  )}
                  {isAdmin && showDetail.status === "draft" && (
                    <Button size="sm" variant="outline" onClick={() => handleAction(showDetail.id, "submit")} data-testid="wo-detail-submit">
                      <PaperPlaneTilt size={14} className="mr-1" /> Submit for Approval
                    </Button>
                  )}
                  {isAdmin && showDetail.status === "submitted" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleAction(showDetail.id, "approve")} data-testid="wo-detail-approve">
                      <Check size={14} className="mr-1" weight="bold" /> Approve
                    </Button>
                  )}
                  {isAdmin && showDetail.status === "approved" && (
                    <Button size="sm" onClick={() => handleAction(showDetail.id, "send")} data-testid="wo-detail-send">
                      <ArrowRight size={14} className="mr-1" /> Send to Supplier
                    </Button>
                  )}
                  {showDetail.status === "sent_to_vendor" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleAction(showDetail.id, "complete")} data-testid="wo-detail-complete">
                      <Check size={14} className="mr-1" weight="bold" /> Mark Complete
                    </Button>
                  )}
                  {isAdmin && ["approved", "sent_to_vendor", "completed"].includes(showDetail.status) && (
                    <Button size="sm" variant="outline" className="text-orange-600 border-orange-300" onClick={() => openAmendDialog(showDetail)} data-testid="wo-detail-amend">
                      <PencilSimple size={14} className="mr-1" /> Amend
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => fetchRevisions(showDetail.id)} data-testid="wo-detail-revisions">
                    <GitBranch size={14} className="mr-1" /> Revisions
                  </Button>
                  {isAdmin && showDetail.vendor_email && (
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        await API.post(`/works-orders/${showDetail.id}/email-supplier`);
                        toast.success(`Works Order emailed to ${showDetail.vendor_email}`);
                      } catch (err) { toast.error(err.response?.data?.detail || "Failed to email"); }
                    }} data-testid="wo-detail-email-supplier">
                      <EnvelopeSimple size={14} className="mr-1" /> Email to Supplier
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Amendment Dialog */}
      <Dialog open={!!showAmend} onOpenChange={(open) => { if (!open) setShowAmend(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="amend-wo-dialog">
          {showAmend && (
            <form onSubmit={handleAmend} className="space-y-4">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Amend Works Order</DialogTitle>
                <DialogDescription>
                  Create a new revision of <span className="font-mono font-semibold">{showAmend.wo_number}</span>. The original will be marked as superseded and a new draft (e.g. {showAmend.wo_number.split("-R")[0]}-R{(showAmend.revision_number || 0) + 1}) will be created.
                </DialogDescription>
              </DialogHeader>
              <div>
                <Label>Reason for Amendment *</Label>
                <Textarea value={amendForm.amendment_reason} onChange={e => setAmendForm({...amendForm, amendment_reason: e.target.value})} rows={2} required placeholder="Why is this WO being amended?" data-testid="amend-reason-input" />
              </div>
              <div>
                <Label>Updated Scope</Label>
                <Textarea value={amendForm.scope} onChange={e => setAmendForm({...amendForm, scope: e.target.value})} rows={3} data-testid="amend-scope-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={amendForm.priority} onValueChange={v => setAmendForm({...amendForm, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["low","medium","high","critical"].map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Due Date</Label><DateInput value={amendForm.due_date} onChange={e => setAmendForm({...amendForm, due_date: e.target.value})} /></div>
              </div>
              <div>
                <Label>Updated Instructions</Label>
                <Textarea value={amendForm.instructions} onChange={e => setAmendForm({...amendForm, instructions: e.target.value})} rows={2} />
              </div>
              <p className="text-xs text-muted-foreground">Note: Task line items and budgets will be re-pulled from the latest supplier task data.</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAmend(null)}>Cancel</Button>
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="submit-amend-btn">Create Amendment</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Revisions Dialog */}
      <Dialog open={!!showRevisions} onOpenChange={(open) => { if (!open) setShowRevisions(null); }}>
        <DialogContent className="max-w-md" data-testid="revisions-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Revision History</DialogTitle>
            <DialogDescription>All versions of this works order</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="revisions-list">
            {revisions.map((rev, idx) => (
              <div key={rev.id} className={`flex items-center justify-between p-2.5 rounded border text-xs ${rev.status === "superseded" ? "border-border opacity-60" : "border-primary/30 bg-primary/5"}`} data-testid={`revision-item-${idx}`}>
                <div>
                  <span className="font-mono font-medium">{rev.wo_number}</span>
                  {rev.revision_number > 0 && <span className="ml-1.5 text-[9px] font-medium px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">R{rev.revision_number}</span>}
                  <p className="text-muted-foreground mt-0.5">Budget: £{(rev.total_budget || 0).toLocaleString()} | By: {rev.created_by_name}</p>
                  {rev.amendment_reason && <p className="text-muted-foreground italic">"{rev.amendment_reason}"</p>}
                </div>
                <div className="text-right">
                  <StatusBadge status={rev.status} />
                  <p className="text-muted-foreground mt-0.5">{fmtDate(rev.created_at)}</p>
                </div>
              </div>
            ))}
            {revisions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No revision history found</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Works Order Confirmation Dialog */}
      <Dialog open={!!deleteWO} onOpenChange={(open) => { if (!open) { setDeleteWO(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-wo-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo] text-destructive">Delete Works Order Permanently</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This action cannot be undone. This will permanently remove works order <span className="font-semibold text-foreground">{deleteWO?.wo_number}</span> from the system.
            </p>
            <div className="border border-border rounded-md p-3 bg-muted/30 space-y-1 text-xs">
              <p><span className="font-semibold">WO Number:</span> {deleteWO?.wo_number}</p>
              <p><span className="font-semibold">Project:</span> {deleteWO?.project_name}</p>
              <p><span className="font-semibold">Supplier:</span> {deleteWO?.vendor_name}</p>
              <p><span className="font-semibold">Status:</span> {deleteWO?.status?.replace(/_/g, " ")}</p>
            </div>
            <div>
              <Label className="text-xs">Type <span className="font-bold text-destructive">DELETE</span> to confirm</Label>
              <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE to confirm" data-testid="delete-wo-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteWO(null); setDeleteConfirmText(""); }}>Cancel</Button>
            <Button variant="destructive" disabled={deleteConfirmText !== "DELETE"} onClick={handleDeleteWO} data-testid="confirm-delete-wo-btn">Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Works Order Editor (draft WOs only) */}
      <WorkOrderEditDialog
        wo={editingWO}
        onClose={() => setEditingWO(null)}
        onSaved={(fresh) => {
          if (fresh) setShowDetail(prev => (prev && prev.id === fresh.id ? fresh : prev));
          fetchWOs();
        }}
      />

      {/* New off-system supplier for the create wizard */}
      <NewSupplierDialog
        open={showNewSupplier}
        onClose={() => setShowNewSupplier(false)}
        testIdPrefix="wo-new-supplier"
        actionWord="Use"
        description="Enter the details of a supplier who isn't on the system yet. They'll be added to your supplier list and used on this works order."
        onCreated={(created, invited) => {
          setSuppliers(prev => [created, ...prev]);
          setForm(f => ({
            ...f,
            supplier_record_id: created.id,
            vendor_id: created.user_id || "",
            vendor_name: created.contact_name || "",
            vendor_company: created.company_name || "",
            vendor_email: created.email || "",
            vendor_phone: created.phone || "",
          }));
          toast.success(`${created.company_name} added to your suppliers${invited ? " — portal invite sent" : ""}`);
        }}
      />

      {/* Branded PDF Dialog */}
      <Dialog open={!!brandedFor} onOpenChange={(open) => { if (!open) setBrandedFor(null); }}>
        <DialogContent className="max-w-md" data-testid="wo-branded-pdf-dialog">
          {brandedFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Branded PDF — <span className="font-mono">{brandedFor.wo_number}</span></DialogTitle>
                <DialogDescription>
                  Override your tenant branding for this download. Leave fields blank to use your saved branding.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Company Name</Label>
                  <Input value={brandedForm.company_name} onChange={(e) => setBrandedForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Your company name" data-testid="wo-branded-company" />
                </div>
                <div>
                  <Label className="text-xs">Tagline (optional)</Label>
                  <Input value={brandedForm.tagline} onChange={(e) => setBrandedForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Property maintenance specialists" data-testid="wo-branded-tagline" />
                </div>
                <div>
                  <Label className="text-xs">Logo</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {brandedForm.logo_url && (
                      <img src={brandedForm.logo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${brandedForm.logo_url}` : brandedForm.logo_url} alt="logo" className="h-10 w-10 object-contain border border-border rounded" data-testid="wo-branded-logo-preview" />
                    )}
                    <label className="flex-1">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBrandedLogoUpload(e.target.files?.[0])} data-testid="wo-branded-logo-input" />
                      <Button type="button" variant="outline" size="sm" disabled={brandedUploading} asChild>
                        <span className="cursor-pointer">
                          <UploadIcon size={14} className="mr-1" />
                          {brandedUploading ? "Uploading..." : brandedForm.logo_url ? "Replace logo" : "Upload logo"}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Footer Text (optional)</Label>
                  <Input value={brandedForm.footer_text} onChange={(e) => setBrandedForm(f => ({ ...f, footer_text: e.target.value }))} placeholder="Reg. office: ... | VAT: ..." data-testid="wo-branded-footer" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBrandedFor(null)}>Cancel</Button>
                <Button onClick={downloadBrandedPdf} disabled={brandedDownloading} data-testid="wo-branded-download-btn">
                  <DownloadSimple size={14} className="mr-1" />
                  {brandedDownloading ? "Generating..." : "Download Branded PDF"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Send to Supplier Dialog */}
      <Dialog open={!!sendingFor} onOpenChange={(open) => { if (!open) setSendingFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="wo-send-supplier-dialog">
          {sendingFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Send to Supplier — <span className="font-mono">{sendingFor.wo_number}</span></DialogTitle>
                <DialogDescription>
                  Issue this Works Order to the supplier via email (with branded PDF attached) and/or push it straight into their portal if they&apos;re onboarded.
                </DialogDescription>
              </DialogHeader>

              {/* Recipient summary */}
              <div className="border border-border rounded-md p-3 bg-muted/30 text-sm space-y-1" data-testid="wo-send-recipient">
                <div className="flex items-center gap-2 font-medium">
                  <Storefront size={14} className="text-blue-600" />
                  <span>{supplierMeta.company_name || sendingFor.vendor_name || "Unknown supplier"}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {supplierMeta.email
                    ? <>Email: <span className="font-mono">{supplierMeta.email}</span></>
                    : <span className="text-amber-600">No email on file</span>}
                </p>
                {supplierMeta.loaded && (
                  <div className="flex items-center gap-1.5 text-xs pt-1">
                    {supplierMeta.has_portal ? (
                      <>
                        <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                        <span className="text-emerald-700 dark:text-emerald-400">Onboarded — portal delivery available</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={14} weight="fill" className="text-muted-foreground" />
                        <span className="text-muted-foreground">Not onboarded on the platform — email only</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Custom message */}
              <div>
                <Label className="text-xs">Optional message (appears in the email + portal notification)</Label>
                <Textarea
                  value={sendForm.custom_message}
                  onChange={(e) => setSendForm(f => ({ ...f, custom_message: e.target.value }))}
                  placeholder="Hi team — please proceed asap and confirm start date."
                  rows={3}
                  className="mt-1"
                  data-testid="wo-send-custom-message"
                />
              </div>

              {/* Delivery channels */}
              <div className="space-y-2">
                <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition ${sendForm.send_email ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" : "border-border bg-muted/20"} ${!supplierMeta.email ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input
                    type="checkbox"
                    checked={sendForm.send_email}
                    disabled={!supplierMeta.email}
                    onChange={(e) => setSendForm(f => ({ ...f, send_email: e.target.checked }))}
                    className="mt-0.5 h-4 w-4"
                    data-testid="wo-send-email-checkbox"
                  />
                  <div className="flex-1 text-sm">
                    <p className="font-medium flex items-center gap-1.5"><EnvelopeSimple size={14} weight="duotone" /> Email with branded PDF</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Sent from your tenant&apos;s branding. PDF attached.</p>
                  </div>
                </label>

                <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition ${sendForm.send_portal ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" : "border-border bg-muted/20"} ${!supplierMeta.has_portal ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input
                    type="checkbox"
                    checked={sendForm.send_portal}
                    disabled={!supplierMeta.has_portal}
                    onChange={(e) => setSendForm(f => ({ ...f, send_portal: e.target.checked }))}
                    className="mt-0.5 h-4 w-4"
                    data-testid="wo-send-portal-checkbox"
                  />
                  <div className="flex-1 text-sm">
                    <p className="font-medium flex items-center gap-1.5"><Storefront size={14} weight="duotone" /> Supplier Portal</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {supplierMeta.has_portal
                        ? "Push to their /supplier-portal — appears in their dashboard + notifications."
                        : "Unavailable — supplier hasn't been onboarded with a portal account yet."}
                    </p>
                  </div>
                </label>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSendingFor(null)}>Cancel</Button>
                <Button
                  onClick={submitSendToSupplier}
                  disabled={sending || (!sendForm.send_email && !sendForm.send_portal)}
                  data-testid="wo-send-confirm-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <PaperPlaneTilt size={14} className="mr-1" />
                  {sending ? "Sending..." : "Send to Supplier"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
